import { useState } from 'react'

import type { TrashEntry } from '../../api/library'
import { asFailure, type Failure } from '../../api/request'
import { Button } from '../../ui/Control'
import { Failed, Waiting } from '../../ui/Status'
import { Offer } from '../../ui/Offer'
import type { Loaded } from '../../api/useLoad'
import { asDate } from './Tiles'

/**
 * The trash: what has been deleted, and the way back.
 *
 * A list and not a shelf of tiles. These are not things one is keeping — they are things one threw
 * away — so what matters about each is what it was called, when it went, and how much would come back
 * with it. A folder's line says "47 documents", which is what makes putting it back a decision somebody
 * can actually take.
 *
 * Two controls per line, and one for the whole trash. *Emptying* is the only place in the library that
 * asks before it acts, because it is the only thing that cannot be undone — everything else in MyLib
 * saves itself and says so on the object.
 */
export interface TrashProps {
  readonly entries: Loaded<TrashEntry[]>
  readonly onRestore: (entry: TrashEntry) => Promise<void>
  readonly onRemove: (entry: TrashEntry) => Promise<void>
  readonly onEmpty: () => Promise<void>
}

export function Trash({ entries, onRestore, onRemove, onEmpty }: TrashProps) {
  const [asking, setAsking] = useState(false)
  const [failure, setFailure] = useState<Failure | null>(null)

  if (entries.load.status === 'waiting') {
    return <Waiting label="Fetching the trash." />
  }
  if (entries.load.status === 'failed') {
    return (
      <Failed
        failure={entries.load.failure}
        onRetry={entries.retry}
        title="The trash did not load"
      />
    )
  }
  if (entries.load.value.length === 0) {
    return (
      <Offer
        title="Nothing has been thrown away"
        lead="Whatever you delete waits here until you empty the trash — so a wrong tap costs nothing, and nothing goes without you saying so twice."
      />
    )
  }

  return (
    <section className="trash" aria-label="Trash">
      {failure !== null && <Failed failure={failure} />}

      <ul className="trash__list">
        {entries.load.value.map((entry) => (
          <li className="trash__item" key={`${entry.kind}-${entry.id}`}>
            <div className="trash__what">
              <p className="trash__name">{entry.name}</p>
              <p className="trash__facts">
                <span>{entry.kind === 'folder' ? 'Folder' : 'Document'}</span>
                <span aria-hidden="true">·</span>
                <span>deleted {asDate(entry.deletedAt)}</span>
                {entry.holds > 0 && (
                  <>
                    <span aria-hidden="true">·</span>
                    <span>
                      {entry.holds === 1 ? '1 document' : `${entry.holds} documents`} would come
                      back with it
                    </span>
                  </>
                )}
              </p>
            </div>
            <div className="trash__doing">
              <Button
                tone="quiet"
                onClick={() => {
                  void onRestore(entry).catch((error: unknown) => {
                    setFailure(asFailure(error))
                  })
                }}
              >
                Put back
              </Button>
              <Button
                tone="bare"
                className="control--danger"
                onClick={() => {
                  void onRemove(entry).catch((error: unknown) => {
                    setFailure(asFailure(error))
                  })
                }}
              >
                Delete for good
              </Button>
            </div>
          </li>
        ))}
      </ul>

      <div className="controls">
        {asking ? (
          <>
            <span className="trash__asking">
              Empty the trash? Everything listed here goes for good, with its files.
            </span>
            <Button
              tone="quiet"
              className="control--danger"
              onClick={() => {
                setAsking(false)
                void onEmpty().catch((error: unknown) => {
                  setFailure(asFailure(error))
                })
              }}
            >
              Empty it
            </Button>
            <Button
              tone="bare"
              onClick={() => {
                setAsking(false)
              }}
            >
              Keep everything
            </Button>
          </>
        ) : (
          <Button
            tone="quiet"
            className="control--danger"
            onClick={() => {
              setAsking(true)
            }}
          >
            Empty the trash
          </Button>
        )}
      </div>
    </section>
  )
}
